import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What are micronutrients and why are they important?",
    options: [
      "Large amounts of nutrients needed for energy production",
      "Essential nutrients needed in small amounts that support vital health processes",
      "Nutrients that make food taste better",
      "Only vitamins, not minerals"
    ],
    correctAnswer: 1,
    explanation: "Micronutrients are vitamins and minerals needed in small amounts but crucial for immune function, energy production, bone health, and countless other vital processes. They work together to keep your body functioning optimally."
  },
  {
    question: "Which of these is a good source of Vitamin A in Filipino cuisine?",
    options: [
      "White rice",
      "Malunggay leaves",
      "Refined sugar",
      "White bread"
    ],
    correctAnswer: 1,
    explanation: "Malunggay (moringa) leaves are an excellent source of Vitamin A, along with carrots and eggs. Filipino cuisine is rich in micronutrients when we include diverse, colorful vegetables and traditional foods."
  },
  {
    question: "What is the primary role of Vitamin C in the body?",
    options: [
      "Energy production",
      "Immune function and skin health",
      "Bone strength",
      "Blood clotting"
    ],
    correctAnswer: 1,
    explanation: "Vitamin C supports immune function and skin health. Good sources include calamansi, guava, and papaya - fruits commonly found in Filipino markets and traditional dishes."
  },
  {
    question: "Which mineral is important for blood health and can be found in malunggay and dried fish?",
    options: [
      "Calcium",
      "Iron",
      "Zinc",
      "Potassium"
    ],
    correctAnswer: 1,
    explanation: "Iron is essential for blood health and oxygen transport. Filipino foods like malunggay, dried fish, and liver are excellent sources of this important mineral."
  },
  {
    question: "What does 'Eat the Rainbow' mean in terms of micronutrients?",
    options: [
      "Eat colorful processed foods",
      "Different colors in fruits and vegetables indicate different nutrients",
      "Only eat rainbow-colored candies",
      "Mix all foods together in one dish"
    ],
    correctAnswer: 1,
    explanation: "Different colors in fruits and vegetables indicate different nutrients. For example, orange foods like carrots provide Vitamin A, green leafy vegetables provide iron and calcium, and red fruits provide antioxidants. Eating a variety of colors ensures you get a broad spectrum of micronutrients."
  }
];

const NutritionalModule3QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('nutritionalM3');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/nutritional-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 3 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>The importance of micronutrients in health</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Vitamin and mineral sources in Filipino foods</span>
              </li>
              <li>
                <span className="check"></span>
                <span>How to incorporate micronutrients into daily meals</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Try incorporating more colorful fruits and vegetables into your meals!</p>
            <p>Ready to continue your wellness journey?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Nutritional Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left nutritional-panel">
          <div className="module-header">
            <h1>Module 3 Quiz</h1>
            <p className="module-subtitle">Meal Planning and Preparation</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about meal planning and preparation.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={currentQuestion === 0 ? handleExit : handleBack}
            >
              {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
            </button>

              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionalModule3QuizPage;

